import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Search,
  Loader2,
  X,
  ChevronRight,
  Sparkles,
  Plus,
  Trash2,
  Save,
  CheckCircle2,
  AlertCircle,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

import { LOGO_URL } from "../config/constants";

const fmt = (v) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(parseFloat(v) || 0);

const num = (v) => parseFloat(v) || 0;
const r2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

const CLIENT_ROLES = [
  { value: "seller", label: "Seller" },
  { value: "buyer", label: "Buyer" },
  { value: "vendor", label: "Vendor" },
  { value: "purchaser", label: "Purchaser" },
  { value: "borrower", label: "Borrower" },
  { value: "transferor", label: "Transferor" },
  { value: "transferee", label: "Transferee" },
];

const TRANSACTION_TYPES = [
  { value: "residential_sale", label: "Residential Sale" },
  { value: "residential_purchase", label: "Residential Purchase" },
  { value: "commercial_sale", label: "Commercial Sale" },
  { value: "commercial_purchase", label: "Commercial Purchase" },
  { value: "share_sale", label: "Share Sale" },
  { value: "share_purchase", label: "Share Purchase" },
  { value: "asset_sale", label: "Business Asset Sale" },
  { value: "asset_purchase", label: "Business Asset Purchase" },
  { value: "remortgage", label: "Remortgage" },
  { value: "transfer_of_equity", label: "Transfer of Equity" },
];

const TENURE_OPTIONS = [
  { value: "freehold", label: "Freehold" },
  { value: "leasehold", label: "Leasehold" },
  { value: "n/a", label: "N/A" },
];

// ---------- Reusable row components ----------

function FeeRow({ item, idx, onChange, onRemove }) {
  const gross = r2(num(item.net) + num(item.vat));
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/60">
      <td className="p-2">
        <Input
          value={item.description}
          onChange={(e) => onChange(idx, "description", e.target.value)}
          placeholder="Description"
          data-testid={`fee-desc-${idx}`}
        />
      </td>
      <td className="p-2">
        <Input
          type="number"
          step="0.01"
          className="w-28 text-right"
          value={item.net || ""}
          onChange={(e) => onChange(idx, "net", e.target.value)}
          data-testid={`fee-net-${idx}`}
        />
      </td>
      <td className="p-2">
        <Input
          type="number"
          step="0.01"
          className="w-24 text-right"
          value={item.vat || ""}
          onChange={(e) => onChange(idx, "vat", e.target.value)}
          data-testid={`fee-vat-${idx}`}
        />
      </td>
      <td className="p-2 text-right font-medium w-28 tabular-nums">{fmt(gross)}</td>
      <td className="p-2 w-10">
        <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="text-status-red hover:text-status-red">
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

function SimpleRow({ item, idx, onChange, onRemove, testPrefix = "simple" }) {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/60">
      <td className="p-2">
        <Input
          value={item.description}
          onChange={(e) => onChange(idx, "description", e.target.value)}
          placeholder="Description"
          data-testid={`${testPrefix}-desc-${idx}`}
        />
      </td>
      <td className="p-2">
        <Input
          type="number"
          step="0.01"
          className="w-32 text-right"
          value={item.amount || ""}
          onChange={(e) => onChange(idx, "amount", e.target.value)}
          data-testid={`${testPrefix}-amount-${idx}`}
        />
      </td>
      <td className="p-2 w-10">
        <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="text-status-red hover:text-status-red">
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

function AdjustmentRow({ item, idx, onChange, onRemove }) {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/60">
      <td className="p-2">
        <Input
          value={item.description}
          onChange={(e) => onChange(idx, "description", e.target.value)}
          placeholder="e.g. Apportioned service charge"
          data-testid={`adj-desc-${idx}`}
        />
      </td>
      <td className="p-2">
        <Input
          type="number"
          step="0.01"
          className="w-28 text-right"
          value={item.amount || ""}
          onChange={(e) => onChange(idx, "amount", e.target.value)}
          data-testid={`adj-amount-${idx}`}
        />
      </td>
      <td className="p-2 w-32">
        <Select value={item.type || "add"} onValueChange={(v) => onChange(idx, "type", v)}>
          <SelectTrigger data-testid={`adj-type-${idx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="add">Add</SelectItem>
            <SelectItem value="deduct">Deduct</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="p-2 w-10">
        <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="text-status-red hover:text-status-red">
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

// ---------- Section component ----------

function ItemSection({ title, items, setItems, RowComponent, emptyFactory, tableHead, addLabel, testId }) {
  const add = () => setItems([...items, emptyFactory()]);
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const change = (idx, field, value) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [field]: value };
    if (field === "net" || field === "vat") {
      copy[idx].gross = r2(num(copy[idx].net) + num(copy[idx].vat));
    }
    setItems(copy);
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-sm font-semibold text-gray-600">{title}</h4>
        <Button variant="outline" size="sm" onClick={add} data-testid={`add-${testId}`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> {addLabel || "Add"}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100 text-left">{tableHead}</tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <RowComponent key={item.id} item={item} idx={idx} onChange={change} onRemove={remove} />
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={10} className="p-4 text-center text-gray-400 italic text-xs">
                  No items. Click "{addLabel || "Add"}" to add.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ========== STEP 1: Matter selection ==========

function StepMatterSelection({ onSelect }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [explanation, setExplanation] = useState("");

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setResults(null);
    setExplanation("");
    try {
      const res = await axios.post(`${API}/hub/ai-search`, { query: query.trim(), limit: 10 });
      setResults(res.data.results || []);
      setExplanation(res.data.explanation || "");
      if (!res.data.results?.length) toast.info("No matters found in CaseMinder or SharePoint.");
    } catch {
      toast.error("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-teal">Step 1 — Select Matter</CardTitle>
          <p className="text-sm text-gray-500">Search for the matter this completion statement relates to.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. 'VEC0089', 'Smith purchase', 'RM0306'"
                className="pr-8 border-purple-200 focus:border-purple-400 focus:ring-purple-400"
                data-testid="cs-matter-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(""); setResults(null); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              type="submit"
              disabled={searching || !query.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
              data-testid="cs-matter-search-btn"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </Button>
          </form>

          {/* Or skip: manual entry */}
          <div className="mb-4 text-center">
            <button
              onClick={() => onSelect(null)}
              className="text-sm text-purple-600 hover:text-purple-800 underline"
              data-testid="cs-skip-matter-btn"
            >
              Or enter details manually without linking to an existing matter
            </button>
          </div>

          {results !== null && (
            <div>
              {explanation && (
                <p className="text-xs text-purple-600 mb-3 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> {explanation}
                </p>
              )}
              {results.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {results.map((r, i) => (
                    <button
                      key={r.id || i}
                      onClick={() => onSelect(r)}
                      className="w-full p-3 bg-white rounded-md border border-purple-100 hover:border-purple-300 hover:shadow-sm transition-all text-left flex items-center justify-between group"
                      data-testid={`cs-matter-result-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                            {r.form_type_label || r.form_type}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            r.status === "checked" || r.status === "authorised" ? "bg-status-green-dim text-status-green" :
                            r.status === "approved" ? "bg-status-green-dim text-status-green" :
                            r.status === "rejected" ? "bg-status-red-dim text-status-red" :
                            "bg-status-amber-dim text-status-amber"
                          }`}>
                            {r.status === "checked" ? "Verified" :
                             r.status === "authorised" ? "Authorised" :
                             r.status === "submitted" ? "Awaiting Check" :
                             r.status === "rejected" ? "Returned" :
                             r.status}
                          </span>
                          {r.source === "sharepoint" && (
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">SharePoint</span>
                          )}
                        </div>
                        <p className="font-medium text-txt-faint truncate">{r.secondary_label}</p>
                        <p className="text-sm text-slate-500 truncate">{r.primary_label}{r.tertiary_label ? ` · ${r.tertiary_label}` : ''}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform flex-shrink-0 ml-2" />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-gray-500 py-4">No results for "{query}".</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ========== STEP 2: Form ==========

function StepForm({ initialMatter, onBack }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [serverTotals, setServerTotals] = useState(null);
  const [isTestSubmission, setIsTestSubmission] = useState(false);

  // --- Header ---
  const [matterReference, setMatterReference] = useState("");
  const [matterId, setMatterId] = useState("");
  const [clientName, setClientName] = useState("");
  const [counterpartyName, setCounterpartyName] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [clientRole, setClientRole] = useState("seller");
  const [transactionType, setTransactionType] = useState("residential_sale");
  const [tenure, setTenure] = useState("freehold");
  const [feeEarner, setFeeEarner] = useState("");

  // --- Consideration ---
  const [considerationAmount, setConsiderationAmount] = useState("");
  const [adjustments, setAdjustments] = useState([]);

  // --- Fees & Taxes ---
  const [legalFees, setLegalFees] = useState([]);
  const [stampDuty, setStampDuty] = useState("");
  const [disbursements, setDisbursements] = useState([]);

  // --- Other Balances ---
  const [redemptions, setRedemptions] = useState([]);
  const [retentions, setRetentions] = useState([]);
  const [paymentsOut, setPaymentsOut] = useState([]);
  const [depositPaid, setDepositPaid] = useState("");
  const [mortgageAdvance, setMortgageAdvance] = useState("");
  const [fundsOnAccount, setFundsOnAccount] = useState("");
  const [otherCredits, setOtherCredits] = useState([]);

  // Pre-populate from FORF via backend prefill endpoint
  useEffect(() => {
    if (!initialMatter) return;

    const matterRef = initialMatter.secondary_label
      || initialMatter.matter_reference
      || initialMatter.data?.Q4
      || initialMatter.data?.Q3
      || initialMatter.data?.header?.matter_reference
      || "";
    
    // Always set the basic info from the search result immediately
    setMatterId(initialMatter.id || "");
    if (initialMatter.primary_label) setClientName(initialMatter.primary_label);

    // If this is a completion statement being revised, pre-populate from its own data
    if (initialMatter.form_type === "completionstatement" && initialMatter.data) {
      const h = initialMatter.data.header || {};
      const c = initialMatter.data.consideration || {};
      setMatterReference(h.matter_reference || matterRef);
      setMatterId(h.matter_id || initialMatter.id || "");
      setClientName(h.client_name || "");
      setCounterpartyName(h.counterparty_name || "");
      setPropertyAddress(h.property_address || "");
      setClientRole(h.client_role || "seller");
      setTransactionType(h.transaction_type || "residential_sale");
      setTenure(h.tenure || "freehold");
      setFeeEarner(h.fee_earner || "");
      if (h.completion_date) setCompletionDate(h.completion_date);
      if (c.amount) setConsiderationAmount(String(c.amount));
      return;
    }

    // Call the prefill endpoint to get the full FORF data
    if (matterRef) {
      axios.get(`${API}/completion-statement/prefill/${encodeURIComponent(matterRef)}`)
        .then((res) => {
          if (res.data.found) {
            const p = res.data.prefill;
            setMatterReference(p.matter_reference || matterRef);
            setMatterId(p.matter_id || initialMatter.id || "");
            setClientName(p.client_name || initialMatter.primary_label || "");
            setCounterpartyName(p.counterparty_name || "");
            setPropertyAddress(p.property_address || "");
            setClientRole(p.client_role || "seller");
            setTransactionType(p.transaction_type || "residential_sale");
            setTenure(p.tenure || "freehold");
            setFeeEarner(p.fee_earner || "");
            if (p.consideration_amount) setConsiderationAmount(String(p.consideration_amount));
            if (p.completion_date) setCompletionDate(p.completion_date);
            toast.success("Header pre-populated from matter intake data");
          } else {
            // No FORF found — use what we have from the search result
            setMatterReference(matterRef);
            toast.info("No matter intake found — please enter details manually");
          }
        })
        .catch(() => {
          setMatterReference(matterRef);
        });
    }
  }, [initialMatter]);

  // --- Build form data for calculation ---
  const formData = useMemo(() => ({
    header: {
      matter_reference: matterReference,
      matter_id: matterId,
      client_name: clientName,
      counterparty_name: counterpartyName,
      property_address: propertyAddress,
      completion_date: completionDate,
      client_role: clientRole,
      transaction_type: transactionType,
      tenure,
      fee_earner: feeEarner,
    },
    consideration: {
      amount: num(considerationAmount),
      adjustments: adjustments.map((a) => ({
        ...a,
        amount: num(a.amount),
      })),
    },
    fees_and_taxes: {
      legal_fees: legalFees.map((f) => ({
        ...f,
        net: num(f.net),
        vat: num(f.vat),
        gross: r2(num(f.net) + num(f.vat)),
      })),
      stamp_duty: num(stampDuty),
      disbursements: disbursements.map((d) => ({
        ...d,
        net: num(d.net),
        vat: num(d.vat),
        gross: r2(num(d.net) + num(d.vat)),
      })),
    },
    other_balances: {
      redemptions: redemptions.map((r) => ({ ...r, amount: num(r.amount) })),
      retentions: retentions.map((r) => ({ ...r, amount: num(r.amount) })),
      payments_out: paymentsOut.map((p) => ({ ...p, amount: num(p.amount) })),
      existing_monies: {
        deposit_paid: num(depositPaid),
        mortgage_advance: num(mortgageAdvance),
        funds_on_account: num(fundsOnAccount),
        other_credits: otherCredits.map((c) => ({ ...c, amount: num(c.amount) })),
      },
    },
  }), [
    matterReference, matterId, clientName, counterpartyName, propertyAddress,
    completionDate, clientRole, transactionType, tenure, feeEarner,
    considerationAmount, adjustments, legalFees, stampDuty, disbursements,
    redemptions, retentions, paymentsOut, depositPaid, mortgageAdvance,
    fundsOnAccount, otherCredits,
  ]);

  // --- Live calculation ---
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/completion-statement/calculate`, formData);
        setServerTotals(res.data);
      } catch { /* ignore calc errors */ }
    }, 350);
    return () => clearTimeout(timer);
  }, [formData]);

  const direction = serverTotals?.direction || infer_direction_local(clientRole);
  const isReceiving = direction === "receiving";

  // --- Submit ---
  const handleSubmit = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const payload = {
        ...formData,
        totals: serverTotals || {},
        notes: "",
        is_test_submission: isTestSubmission,
      };
      const res = await axios.post(`${API}/completion-statement/submit`, payload);
      toast.success(isTestSubmission ? "Test completion statement saved." : "Completion statement submitted for review!");
      navigate(`/submission/${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.validation_errors) {
        setErrors(detail.validation_errors);
      } else {
        setErrors([errorMessage(err, "Failed to save. Please try again.")]);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error banner */}
      {errors.length > 0 && (
        <div className="p-4 bg-status-red-dim border border-red-200 rounded-md">
          <div className="flex items-center gap-2 text-status-red font-medium mb-1">
            <AlertCircle className="h-4 w-4" /> Validation Errors
          </div>
          <ul className="list-disc list-inside text-sm text-status-red space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* ===== Section 1: Header ===== */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-teal">1. Header</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Matter Reference</Label>
              {/* Brief #4 — SmartClientMatterSearch rollout (Block 3 table
                  row #12: feeEarnerScoped=true; the component does not
                  expose a scoping prop, intent captured here as code
                  intent). */}
              <SmartClientMatterSearch
                label={null}
                placeholder="Search clients or matters..."
                initialValue={matterReference}
                onSelect={(result) => {
                  const ref = result.matter_reference || result.label || "";
                  setMatterReference(ref);
                  if (!clientName && result.client_name) setClientName(result.client_name);
                }}
              />
            </div>
            <div>
              <Label>Client Name</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="cs-client-name" />
            </div>
            <div>
              <Label>Counterparty</Label>
              <Input value={counterpartyName} onChange={(e) => setCounterpartyName(e.target.value)} data-testid="cs-counterparty" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Acting For</Label>
              <Select value={clientRole} onValueChange={setClientRole}>
                <SelectTrigger data-testid="cs-client-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLIENT_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Transaction Type</Label>
              <Select value={transactionType} onValueChange={setTransactionType}>
                <SelectTrigger data-testid="cs-transaction-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSACTION_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tenure</Label>
              <Select value={tenure} onValueChange={setTenure}>
                <SelectTrigger data-testid="cs-tenure">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TENURE_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Completion Date</Label>
              <Input type="date" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} data-testid="cs-completion-date" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Property / Subject Matter</Label>
              <Input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} data-testid="cs-property-address" />
            </div>
            <div>
              <Label>Fee Earner</Label>
              <Input value={feeEarner} onChange={(e) => setFeeEarner(e.target.value)} data-testid="cs-fee-earner" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== Section 2: Consideration & Adjustments ===== */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-teal">2. Consideration &amp; Adjustments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>{isReceiving ? "Sale Price / Consideration" : "Purchase Price / Consideration"} (&pound;)</Label>
              <Input
                type="number"
                step="0.01"
                className="text-lg font-semibold"
                value={considerationAmount}
                onChange={(e) => setConsiderationAmount(e.target.value)}
                data-testid="cs-consideration"
              />
            </div>
          </div>
          <ItemSection
            title="Adjustments (apportionments, allowances, etc.)"
            items={adjustments}
            setItems={setAdjustments}
            RowComponent={AdjustmentRow}
            emptyFactory={() => ({ id: uid(), description: "", amount: "", type: "add" })}
            tableHead={<><th className="p-2">Description</th><th className="p-2 w-28 text-right">Amount (&pound;)</th><th className="p-2 w-32">Direction</th><th className="p-2 w-10"></th></>}
            addLabel="Add Adjustment"
            testId="adjustment"
          />
        </CardContent>
      </Card>

      {/* ===== Section 3: Fees, Taxes & Disbursements ===== */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-teal">3. Fees, VAT, Stamp Duty &amp; Disbursements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ItemSection
            title="Legal Fees"
            items={legalFees}
            setItems={setLegalFees}
            RowComponent={FeeRow}
            emptyFactory={() => ({ id: uid(), description: "", net: "", vat: "", gross: 0 })}
            tableHead={<><th className="p-2">Description</th><th className="p-2 w-28 text-right">Net (&pound;)</th><th className="p-2 w-24 text-right">VAT (&pound;)</th><th className="p-2 w-28 text-right">Gross</th><th className="p-2 w-10"></th></>}
            addLabel="Add Fee"
            testId="legal-fee"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-2">
            <div>
              <Label>Stamp Duty (SDLT / LTT) (&pound;)</Label>
              <Input
                type="number"
                step="0.01"
                value={stampDuty}
                onChange={(e) => setStampDuty(e.target.value)}
                data-testid="cs-stamp-duty"
              />
            </div>
          </div>
          <ItemSection
            title="Disbursements / Third-party Costs"
            items={disbursements}
            setItems={setDisbursements}
            RowComponent={FeeRow}
            emptyFactory={() => ({ id: uid(), description: "", net: "", vat: "", gross: 0 })}
            tableHead={<><th className="p-2">Description</th><th className="p-2 w-28 text-right">Net (&pound;)</th><th className="p-2 w-24 text-right">VAT (&pound;)</th><th className="p-2 w-28 text-right">Gross</th><th className="p-2 w-10"></th></>}
            addLabel="Add Disbursement"
            testId="disbursement"
          />
        </CardContent>
      </Card>

      {/* ===== Section 4: Other Payments & Balances ===== */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-teal">4. Other Payments &amp; Balances</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ItemSection
            title="Redemptions (Mortgages, Charges)"
            items={redemptions}
            setItems={setRedemptions}
            RowComponent={(props) => <SimpleRow {...props} testPrefix="redemption" />}
            emptyFactory={() => ({ id: uid(), description: "", amount: "" })}
            tableHead={<><th className="p-2">Description</th><th className="p-2 w-32 text-right">Amount (&pound;)</th><th className="p-2 w-10"></th></>}
            addLabel="Add Redemption"
            testId="redemption"
          />
          <ItemSection
            title="Retentions"
            items={retentions}
            setItems={setRetentions}
            RowComponent={(props) => <SimpleRow {...props} testPrefix="retention" />}
            emptyFactory={() => ({ id: uid(), description: "", amount: "" })}
            tableHead={<><th className="p-2">Description</th><th className="p-2 w-32 text-right">Amount (&pound;)</th><th className="p-2 w-10"></th></>}
            addLabel="Add Retention"
            testId="retention"
          />
          <ItemSection
            title="Other Payments Out (Estate agent, etc.)"
            items={paymentsOut}
            setItems={setPaymentsOut}
            RowComponent={(props) => <SimpleRow {...props} testPrefix="payment-out" />}
            emptyFactory={() => ({ id: uid(), description: "", amount: "" })}
            tableHead={<><th className="p-2">Description</th><th className="p-2 w-32 text-right">Amount (&pound;)</th><th className="p-2 w-10"></th></>}
            addLabel="Add Payment"
            testId="payment-out"
          />

          <div className="pt-4 border-t">
            <h4 className="text-sm font-semibold text-gray-600 mb-3">Existing Monies / Funds Available</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label>Deposit Paid (&pound;)</Label>
                <Input type="number" step="0.01" value={depositPaid} onChange={(e) => setDepositPaid(e.target.value)} data-testid="cs-deposit" />
              </div>
              <div>
                <Label>Mortgage Advance (&pound;)</Label>
                <Input type="number" step="0.01" value={mortgageAdvance} onChange={(e) => setMortgageAdvance(e.target.value)} data-testid="cs-mortgage" />
              </div>
              <div>
                <Label>Funds on Client Account (&pound;)</Label>
                <Input type="number" step="0.01" value={fundsOnAccount} onChange={(e) => setFundsOnAccount(e.target.value)} data-testid="cs-funds-on-account" />
              </div>
            </div>
            <ItemSection
              title="Other Credits / Funds"
              items={otherCredits}
              setItems={setOtherCredits}
              RowComponent={(props) => <SimpleRow {...props} testPrefix="credit" />}
              emptyFactory={() => ({ id: uid(), description: "", amount: "" })}
              tableHead={<><th className="p-2">Description</th><th className="p-2 w-32 text-right">Amount (&pound;)</th><th className="p-2 w-10"></th></>}
              addLabel="Add Credit"
              testId="other-credit"
            />
          </div>
        </CardContent>
      </Card>

      {/* ===== Section 5: Summary Totals ===== */}
      {serverTotals && (
        <Card className="border-teal/30 bg-gradient-to-br from-teal-dim/60 to-teal-dim/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-teal">5. Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <Row label={isReceiving ? "Sale Price / Consideration" : "Purchase Price / Consideration"} value={fmt(serverTotals.consideration_amount)} />
              {(serverTotals.adjustments_add > 0 || serverTotals.adjustments_deduct > 0) && (
                <Row label="Adjustments (net)" value={(serverTotals.adjustments_net >= 0 ? "+" : "") + fmt(serverTotals.adjustments_net)} sub />
              )}
              <Row label="Legal Fees" value={fmt(serverTotals.legal_fees_gross)} sub />
              {serverTotals.stamp_duty > 0 && <Row label="Stamp Duty" value={fmt(serverTotals.stamp_duty)} sub />}
              <Row label="Disbursements" value={fmt(serverTotals.disbursements_gross)} sub />
              {serverTotals.redemptions_total > 0 && <Row label="Redemptions" value={fmt(serverTotals.redemptions_total)} sub />}
              {serverTotals.retentions_total > 0 && <Row label="Retentions" value={fmt(serverTotals.retentions_total)} sub />}
              {serverTotals.payments_out_total > 0 && <Row label="Other Payments Out" value={fmt(serverTotals.payments_out_total)} sub />}
              {serverTotals.total_existing_monies > 0 && (
                <Row label="Existing Monies / Funds" value={fmt(serverTotals.total_existing_monies)} sub green />
              )}
              <div className="pt-3 mt-3 border-t-2 border-teal/30">
                <div className="flex justify-between items-center p-3 rounded-md bg-teal text-white" data-testid="cs-net-balance">
                  <span className="text-base font-bold">
                    {serverTotals.balance_direction === "to_client"
                      ? "Balance due to you on completion"
                      : "Balance required from you on completion"}
                  </span>
                  <span className="text-xl font-bold tabular-nums">
                    {fmt(serverTotals.net_completion_balance)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submit */}
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={onBack} data-testid="cs-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Matter Selection
        </Button>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer" data-testid="cs-test-toggle">
            <input
              type="checkbox"
              checked={isTestSubmission}
              onChange={(e) => setIsTestSubmission(e.target.checked)}
              className="rounded border-gray-300"
            />
            Test submission
          </label>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-teal hover:bg-teal-hover text-white gap-2 px-8"
            data-testid="cs-submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving..." : "Submit Completion Statement"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, sub, green }) {
  return (
    <div className={`flex justify-between py-1.5 ${sub ? "pl-4 text-gray-600" : "font-medium"} ${green ? "text-teal" : ""}`}>
      <span>{label}</span>
      <span className="tabular-nums font-medium">{value}</span>
    </div>
  );
}

function infer_direction_local(role) {
  if (["seller", "vendor", "transferor"].includes(role)) return "receiving";
  return "paying";
}

// ========== Main Page ==========

export default function CompletionStatementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedMatter, setSelectedMatter] = useState(null);

  // If arriving with ?matter_id=... pre-select
  useEffect(() => {
    const mid = searchParams.get("matter_id");
    if (mid) {
      axios.get(`${API}/submissions/${mid}`).then((res) => {
        const sub = res.data;
        // Normalize: ensure secondary_label and matter_reference exist at root
        const normalized = {
          ...sub,
          matter_reference: sub.matter_reference || sub.data?.Q4 || sub.data?.Q3 || "",
          secondary_label: sub.secondary_label || sub.matter_reference || sub.data?.Q4 || sub.data?.Q3 || "",
          primary_label: sub.primary_label || sub.data?.Q1 || sub.data?.header?.client_name || "",
          tertiary_label: sub.tertiary_label || sub.data?.Q8_property_address || sub.data?.header?.property_address || "",
        };
        setSelectedMatter(normalized);
        setStep(2);
      }).catch(() => {});
    }
  }, [searchParams]);

  const handleMatterSelect = (matter) => {
    setSelectedMatter(matter);
    setStep(2);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/")} className="flex items-center gap-2 hover:opacity-80">
                <img src={LOGO_URL} alt="Bonsai Law" className="w-8 h-8" />
                <span className="text-sm font-bold text-teal">Bonsai Law</span>
              </button>
              <span className="text-gray-300">/</span>
              <span className="text-sm font-medium text-gray-700">Completion Statement</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="cs-back-dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step indicator */}
        <div className="flex items-center gap-3 mb-8">
          <StepIndicator num={1} label="Select Matter" active={step === 1} done={step > 1} />
          <div className="h-px flex-1 bg-gray-300" />
          <StepIndicator num={2} label="Complete Form" active={step === 2} done={false} />
        </div>

        {step === 1 && <StepMatterSelection onSelect={handleMatterSelect} />}
        {step === 2 && <StepForm initialMatter={selectedMatter} onBack={() => setStep(1)} />}
      </main>
    </div>
  );
}

function StepIndicator({ num, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold ${
          done ? "bg-teal text-white" : active ? "bg-teal text-white" : "bg-gray-200 text-gray-500"
        }`}
      >
        {done ? <CheckCircle2 className="w-4 h-4" /> : num}
      </div>
      <span className={`text-sm ${active || done ? "font-medium text-gray-900" : "text-gray-400"}`}>{label}</span>
    </div>
  );
}
